import { FunctionComponent, useMemo} from "react";
import styles from "./PublishPopupSingle.module.css";
import { CustomTextField, InputPropsStyleForInvoice } from "../../../helper";
import { Button, InputAdornment } from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import ToasterWithMessage from "../toaster";
import images from "../../../assets/images";

interface NumberEditPopupProps {
  title: string;
  label: string;
  prefix?: string;
  defaultValue: number | string  | undefined;
  maxValue?: number;
  minValue?: number;
  onSubmit: (value: number) => void;
  onClose: () => void;
}

const InputField = CustomTextField();

export const NumberEditPopup: FunctionComponent<NumberEditPopupProps> = ({
  title,
  label,
  prefix,
  defaultValue,
  maxValue = 100000,
  minValue = 0,
  onSubmit,
  onClose,
}) => {
  const {
    handleSubmit,
    watch,
    reset,
    formState: { errors },
    control,
    setError,
  } = useForm({
    defaultValues: { value: String(defaultValue) },
    mode: "onChange",
  });
  const { error } = ToasterWithMessage();
  const { CloseIcon } = images;
  const handleFormSubmit = (data: { value: string }) => {
    if (!data.value) {
    setError("value", {
      type: "manual",
      message: `${label} is required`,
    });
    return;
  }
    const numeric = parseFloat(data.value);

    if (numeric === parseFloat(String(defaultValue))) {
      return error("Please edit anything to update!");
    }

    if (numeric <= 0) {
      setError("value", { type: "manual", message: "Value must be greater than zero" });
      return;
    }

    if (numeric > maxValue || numeric < minValue) {
      setError("value", { type: "manual", message: `Value must be between ${minValue} and ${maxValue}` });
      return;
    }

    onSubmit(numeric);
  };

  const watchValue = watch("value");
  const disableBtn = useMemo(() => {
  if (!watchValue) return true;

  const numeric = parseFloat(watchValue);

  if (isNaN(numeric)) return true;
  if (numeric === Number(defaultValue)) return true;
  if (numeric <= 0) return true;
  if (numeric < minValue || numeric > maxValue) return true;

  return false;
}, [watchValue, defaultValue, minValue, maxValue]);

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)} autoComplete="off" style={{padding:"10px",gap:"20px",display:"flex",flexDirection:"column"}}>
      <div className={styles.markAsSold} style={{gap:"50px"}}>
        <div className={styles.title}>
          <b className={styles.markAsSoldTitle}>{title}</b>
          <div className={styles.closeicon}>
            <img src={CloseIcon} alt="closeicon" onClick={onClose} />
          </div>
        </div>
        <div className={styles.logitixSoldPrice}>
          <div className={styles.bg}>
            <div className={styles.eventDetailsPrice}>
              <div className={styles.soldPrice}>
                <div className={styles.transactionId}>
                  <b className={styles.date} >{`Enter ${label==='Percentage' ? `${label}\n( Including logitix % )`:label} :`}</b>
                  <span className={styles.errorMsgListPriceStyle}>
                    {errors?.value?.message}
                  </span>
                </div>

                 <div className={styles.textField}>
                    <div className={styles.container}></div>
                    <Controller
                      name="value"
                      control={control}
                      rules={{ 
                        required: `${label} is required`,
                        min: { value: minValue, message: `Value must be at least ${minValue}` },
                        max: { value: maxValue, message: `Value must be at most ${maxValue}` },
                        validate: (val) => {
                          const numeric = parseFloat(val);
                          if (isNaN(numeric)) return "Invalid number";
                          if (numeric <= 0) return "Value must be greater than zero";
                          return true;
                        }, 
                      }}
                      render={({ field,fieldState }) => (
                      <InputField
                      {...field}
                      onChange={(e) => {
                        const input = e.target.value;
                        if (input === "") {
                          field.onChange("");
                          return;
                        }
                        const regex = /^(0|[1-9]\d*)(\.\d{0,2})?$/;
                        if (regex.test(input)) {
                          field.onChange(input);
                        } else {
                            setError("value", {
                              type: "manual",
                              message: "Only numbers with up to 2 decimals are allowed",
                            });
                      }}}
                      error={!!fieldState.error}
                      placeholder={`Eg: ${prefix ?? ""}${defaultValue}`}
                      InputProps={{
                        startAdornment: prefix ? (
                          <InputAdornment position="start">{prefix}</InputAdornment>
                        ) : null,
                        style: InputPropsStyleForInvoice,
                      }}
                      sx={{ "& input": { height: "0.0375em",width: "100px" } }}
                      />
                    )}
                    />
                    </div>                
              </div>
            </div>
          </div>
        </div>

        <div className={styles.cta}>
          <div className={styles.buttons}>
            <Button
              classes={{ root: styles.separateStyleforcancel }}
              fullWidth
              disableRipple
              onClick={() => {
                reset({value: ""});
              }}
            >
              Reset
            </Button>
          </div>
          <div className={disableBtn?styles.disabledButton:styles.buttons1}>
            <Button
               classes={{ root: disableBtn 
                  ? styles.separateStyledisabledUpdateBtn
                  : styles.separateStyle }}
              disableRipple
              fullWidth
              type="submit"
              disabled={disableBtn}
            >
              Update
            </Button>
          </div>
        </div>
      </div>
    </form>
  );
};


